import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

/**
 * Visual-only "highlight" state — separate from `selectedShapes` so that
 * clicking a route line to highlight it + its 100+ associated stops does
 * NOT trigger a re-render of every `selectedShapes` consumer (Layer Panel,
 * shape rows, attribute table, …).
 *
 * Only renderers that need to draw a highlight ring/halo should subscribe.
 */
interface HighlightContextValue {
    isHighlighted: (id: string) => boolean;
    highlightedIds: ReadonlySet<string>;
    setHighlighted: (ids: string[]) => void;
    clearHighlight: () => void;
}

const HighlightContext = createContext<HighlightContextValue | null>(null);

export const HighlightProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [highlightedIds, setHighlightedIds] = useState<ReadonlySet<string>>(() => new Set());

    // Stable callbacks so descendants memoizing on them won't churn.
    const setHighlighted = useCallback((ids: string[]) => {
        setHighlightedIds(new Set(ids));
    }, []);
    const clearHighlight = useCallback(() => {
        setHighlightedIds(prev => (prev.size === 0 ? prev : new Set()));
    }, []);

    // `isHighlighted` reads from a ref so it doesn't have to change identity
    // when the set updates — callers that close over it stay stable.
    const idsRef = useRef(highlightedIds);
    idsRef.current = highlightedIds;
    const isHighlighted = useCallback((id: string) => idsRef.current.has(id), []);

    const value = useMemo<HighlightContextValue>(
        () => ({ isHighlighted, highlightedIds, setHighlighted, clearHighlight }),
        [isHighlighted, highlightedIds, setHighlighted, clearHighlight]
    );

    return <HighlightContext.Provider value={value}>{children}</HighlightContext.Provider>;
};

export function useHighlight(): HighlightContextValue {
    const ctx = useContext(HighlightContext);
    if (!ctx) {
        // Allow components to be rendered outside the provider without throwing —
        // they just get a no-op highlight stub. Useful for tests / standalone use.
        return {
            isHighlighted: () => false,
            highlightedIds: new Set(),
            setHighlighted: () => undefined,
            clearHighlight: () => undefined,
        };
    }
    return ctx;
}
